import React, { useState, useEffect, useCallback } from "react";
import { connect } from "react-redux";
import { useForm } from "react-hook-form";
import { IoIosEye, IoIosEyeOff } from "react-icons/io";
import { yupResolver } from "@hookform/resolvers/yup";
import { useSearchParams, useNavigate } from "react-router-dom";
import * as Yup from "yup";

import LogoIcon from "../../assets/images/logo-icon.png";
import { authConstants, VERIFICATION_TOKEN } from "../../constants";
import BaseModal from "../../shared/components/modals/BaseModal";
import MainSpinner from "../../shared/components/spinner/MainSpinner";
import RoundedButton from "../../shared/components/buttons/RoundedButton";
import { authActions } from "../../redux/actions";

const schema = Yup.object().shape({
  newPassword: Yup.string()
    .required("Password is required")
    .min(8, "Password must be at least 8 characters")
    .matches(
      /^(?=.*[a-zA-Z])(?=.*\d)(?=.*[!@#$%^&*()_+[\]{};':"\\|,.<>/?`~])[a-zA-Z\d!@#$%^&*()_+[\]{};':"\\|,.<>/?`~]+$/,
      "Password must include letters, numbers, and at least one special character"
    ),

  confirmPassword: Yup.string()
    .oneOf([Yup.ref("newPassword"), null], "Passwords must match")
    .required("Confirm Password is required"),
});

const ResetForm = ({
  verifyUser,
  verifyEmail,
  resetPassword,
  spinnerActions,
  setIsNavigateToDefault,
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const encryptedParams = searchParams.get(VERIFICATION_TOKEN);

  const [openModal, setOpenModal] = useState(true);
  const [showPassword, setShowPassword] = useState({
    newPassword: false,
    confirmPassword: false,
  });

  const spinner = useCallback(
    (type) => spinnerActions.findIndex((s) => s === type),
    // eslint-disable-next-line
    [spinnerActions]
  );

  const handleShowPassword = (field) => {
    setShowPassword((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const closeModal = () => {
    setOpenModal(false);
    navigateToLogin();
  };

  // Use yupResolver to integrate Yup with React Hook Form
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema), // Use the Yup schema for validation
  });

  const navigateToLogin = () => {
    navigate("/", {
      replace: true,
    });
    // Delay the state update
    setTimeout(() => {
      setIsNavigateToDefault(true);
    }, 0);
  };

  const onSubmit = (data) => {
    const dataToSend = {
      password: data.newPassword,
      confirmPassword: data.confirmPassword,
      encryptedParameters: encryptedParams,
    };

    resetPassword(dataToSend, navigateToLogin);
  };

  // Use effect that creates object from values from params and verifies it
  useEffect(() => {
    if (encryptedParams) {
      verifyEmail(encryptedParams);
    } else if (!encryptedParams && !encryptedParams?.userId) {
      navigateToLogin();
    }
    // eslint-disable-next-line
  }, []);

  return (
    <>
      {spinner(authConstants.VERIFY_EMAIL) > -1 ? (
        <MainSpinner />
      ) : (
        <BaseModal
          size="xs"
          showClose={true}
          show={openModal}
          fullScreen={false}
          onClose={closeModal}
          backDropClass="login-modal-backdrop"
          dialogClassName="login-modal-dialog"
        >
          <div className="forgot-form">
            <div className="d-flex justify-content-center mb-5">
              <img src={LogoIcon} alt="Bayview Shores" height={49} />
              <p className="login-brand-logo">Bayview Shores</p>
            </div>
            {verifyUser?.userId ? (
              <>
                <div className="form-heading">Reset Password</div>
                <div className="forgot-desc reset-desc">
                  Set your new password to login into your account!
                </div>
                <form onSubmit={handleSubmit(onSubmit)}>
                  <div className="position-relative">
                    <input
                      className={`auth-form-input  mt-4 ${
                        errors.newPassword ? "is-invalid" : ""
                      }`}
                      placeholder="Enter new password"
                      type={showPassword.newPassword ? "text" : "password"}
                      {...register("newPassword")}
                    />
                    {showPassword.newPassword ? (
                      <IoIosEye
                        className="position-absolute reset-field-icon reset-field-icon-password pointer"
                        onClick={() => handleShowPassword("newPassword")}
                      />
                    ) : (
                      <IoIosEyeOff
                        className="position-absolute reset-field-icon reset-field-icon-password pointer"
                        onClick={() => handleShowPassword("newPassword")}
                      />
                    )}
                  </div>
                  {errors.newPassword && (
                    <div className="error">{errors.newPassword.message}</div>
                  )}
                  <div className="position-relative">
                    <input
                      className={`auth-form-input  mt-4 ${
                        errors.confirmPassword ? "is-invalid" : ""
                      }`}
                      placeholder="Confirm password"
                      type={showPassword.confirmPassword ? "text" : "password"}
                      {...register("confirmPassword")}
                    />
                    {showPassword.confirmPassword ? (
                      <IoIosEye
                        className="position-absolute reset-field-icon reset-field-icon-password pointer"
                        onClick={() => handleShowPassword("confirmPassword")}
                      />
                    ) : (
                      <IoIosEyeOff
                        className="position-absolute reset-field-icon reset-field-icon-password pointer"
                        onClick={() => handleShowPassword("confirmPassword")}
                      />
                    )}
                  </div>
                  {errors.confirmPassword && (
                    <div className="error">
                      {errors.confirmPassword.message}
                    </div>
                  )}
                  <RoundedButton
                    cotainerClass="d-flex justify-content-center reset-form-bottom-button"
                    className="auth-button mx-auto bg-light border-light w-65 py-2"
                    type="submit"
                    disabled={spinner(authConstants.RESET) > -1}
                  >
                    {spinner(authConstants.RESET) > -1
                      ? "Submitting..."
                      : "Confirm"}
                  </RoundedButton>
                </form>
              </>
            ) : (
              <>
                <p className="pt-5 text-center">
                  Your Reset Password Link has been expired.
                  <br />
                  Please contact with support team.
                </p>
              </>
            )}
          </div>
        </BaseModal>
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  verifyUser: state.authReducer.verifyUser,
  spinnerActions: state.systemReducer.spinnerActions,
});

const mapDispatchToProps = (dispatch) => ({
  verifyEmail: (body) => dispatch(authActions.verifyEmail(body)),
  resetPassword: (data, onSuccess) =>
    dispatch(authActions.resetPassword(data, onSuccess)),
  setIsNavigateToDefault: (flag) =>
    dispatch(authActions.setIsNavigateToDefault(flag)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ResetForm);
